import {
  getXpP203aBlePickOptions,
  mergeBleOptionalServicesForRequest,
  resolveBleWriteCharacteristic,
  tryEnableIsscAirPatch,
  tryEnableIsscNotifyHandshake,
} from '../blePrinterAutoProfiles';
import type { IsscCreditGate } from '../isscCreditGate';
import {
  shouldPrependXpP203aWake,
  type BleBridgeProfile,
} from '../bleBridgeProfile';
import {
  getXpP203aAttChunkCap,
  recordXpP203aBlePrintSuccess,
  resolveXpP203aBleTiming,
  shouldXpP203aIsscWriteWithResponse,
} from '../xpP203aBleSettings';
import { getEffectivePrinterLinkSettings, getStoredPrinterModelId, type PrinterModelId } from '../printerModelConfig';
import { resolvePrinterBleUuids } from '../printerBleUuidConfig';
import { concatBytes } from '../escpos/commands';
import { previewEscPosBytes, printerDebug, printerDebugWarn } from '../printerDebug';
import { isLikelyTsplPayload, splitTsplPayloadForBleChunks } from '../tspl/tsplBleChunking';
import { previewTsplBytes } from '../tspl/tsplEncode';
import type { PrinterConnectionState } from '../types';
import {
  // ISSC_BLE_CHUNK_BYTES,
  ISSC_BLE_DELAY_ONLY_CHUNK_MS,
  ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS,
  // ISSC_BLE_INTER_CHUNK_DELAY_MS,
  ISSC_BLE_POST_CONNECT_MS,
  // ISSC_BLE_POST_WRITE_SETTLE_MS,
  ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
  ISSC_BLE_UART_FLUSH_MS,
  // XP_P203A_BLE_CHUNK_BYTES,
  // XP_P203A_BLE_INTER_CHUNK_DELAY_MS,
  // XP_P203A_BLE_POST_WRITE_SETTLE_MS,
  XP_P203A_BLE_WAKE_BYTES,
} from '../xpP203aBleConstants';
import type { PrinterTransport } from './PrinterTransport';

const BLE_CHUNK_MIN = 14;
const BLE_CHUNK_MAX = 244;
/** Default ATT payload for write-without-response before MTU exchange. */
const BLE_ATT_WRITE_WITHOUT_RESPONSE_MAX = 20;

const P203A_REQUIRES_WNR_ERROR = 'bluetoothPrinter.errors.p203aRequiresWriteWithoutResponse';

function parsePositiveIntEnv(raw: string | undefined, fallback: number, min: number, max: number): number {
  const n = Number.parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function envTruthy(raw: string | undefined): boolean {
  const s = String(raw ?? '').trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes';
}

function resolveBleWriteChunkBytesFromEnv(): number {
  const explicit = Number.parseInt(String(import.meta.env?.VITE_PRINTER_BLE_CHUNK_BYTES ?? '').trim(), 10);
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.min(BLE_CHUNK_MAX, Math.max(BLE_CHUNK_MIN, explicit));
  }
  const baudHint = Number.parseInt(String(import.meta.env?.VITE_PRINTER_BLE_UART_BAUD ?? '').trim(), 10);
  if (Number.isFinite(baudHint) && baudHint >= 230400) {
    return BLE_CHUNK_MAX;
  }
  return 128;
}

function clampChunkLocal(n: number): number {
  return Math.min(BLE_CHUNK_MAX, Math.max(BLE_CHUNK_MIN, n));
}

function delayMs(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise<void>((r) => {
    globalThis.setTimeout(r, ms);
  });
}

function hexPreview(bytes: Uint8Array, max = 32): string {
  return Array.from(bytes.subarray(0, Math.min(max, bytes.length)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

function summarizeChunkSizes(totalLength: number, step: number): number[] {
  const sizes: number[] = [];
  for (let i = 0; i < totalLength; i += step) {
    sizes.push(Math.min(step, totalLength - i));
  }
  if (sizes.length <= 8) return sizes;
  return [...sizes.slice(0, 3), ...sizes.slice(-1)];
}

export type WebBluetoothTransportOptions = {
  onStateChange?: () => void;
  onGattServerDisconnected?: () => void;
  bleChunkBytes?: number;
  bleInterChunkDelayMs?: number;
  blePreferWriteWithResponse?: boolean;
  blePostWriteSettleMs?: number;
  printerModelId?: PrinterModelId;
};

export type BleTransportDiagnostics = {
  deviceName?: string;
  serviceUuid: string;
  characteristicUuid: string;
  writeMode: 'writeWithoutResponse' | 'writeValue';
  chunkBytes: number;
  interChunkDelayMs: number;
  postWriteSettleMs: number;
  printerModelId?: PrinterModelId;
  writeWithoutResponseAvailable?: boolean;
  bridgeProfile?: BleBridgeProfile;
  wakeOnWrite?: boolean;
  isscNotifyEnabled?: boolean;
  isscCreditFlow?: 'notify' | 'delay-only';
  isscAirPatchEnabled?: boolean;
  explicitUuidFallback?: boolean;
};

export class WebBluetoothTransport implements PrinterTransport {
  readonly kind = 'ble' as const;

  private state: PrinterConnectionState = 'disconnected';

  private device: BluetoothDevice | null = null;

  private characteristic: BluetoothRemoteGATTCharacteristic | null = null;

  private gattDisconnectHandler: (() => void) | null = null;

  private isscNotifyCharacteristic: BluetoothRemoteGATTCharacteristic | null = null;

  private isscCreditGate: IsscCreditGate | null = null;

  /** When true, printer never sent 0x01 credits — pace writes with fixed delays only. */
  private isscDelayOnlyPacing = false;

  private readonly bleChunkBytes: number;

  private readonly bleInterChunkDelayMs: number;

  private readonly blePreferWriteWithResponse: boolean;

  private readonly blePostWriteSettleMs: number;

  /** Model at connect time (for mismatch diagnostics). */
  private readonly connectModelId: PrinterModelId | undefined;

  private bridgeProfile: BleBridgeProfile = 'generic';

  private lastDiagnostics: BleTransportDiagnostics | null = null;

  constructor(private readonly opts: WebBluetoothTransportOptions = {}) {
    const modelAtConstruct = opts.printerModelId ?? getStoredPrinterModelId();
    const isP203a = modelAtConstruct === 'xp-p203a';

    if (isP203a) {
      const link = getEffectivePrinterLinkSettings();
      this.bleChunkBytes = link.bleChunkBytes;
      this.bleInterChunkDelayMs = link.bleInterChunkDelayMs;
      this.blePreferWriteWithResponse = link.bleWriteWithResponse;
      this.blePostWriteSettleMs = link.blePostWriteSettleMs;
    } else {
      this.bleChunkBytes = clampChunkLocal(
        opts.bleChunkBytes !== undefined ? opts.bleChunkBytes : resolveBleWriteChunkBytesFromEnv(),
      );
      this.bleInterChunkDelayMs =
        opts.bleInterChunkDelayMs !== undefined
          ? Math.min(100, Math.max(0, opts.bleInterChunkDelayMs))
          : parsePositiveIntEnv(import.meta.env?.VITE_PRINTER_BLE_INTER_CHUNK_DELAY_MS, 0, 0, 100);
      this.blePreferWriteWithResponse =
        opts.blePreferWriteWithResponse !== undefined
          ? opts.blePreferWriteWithResponse
          : envTruthy(import.meta.env?.VITE_PRINTER_BLE_WRITE_WITH_RESPONSE);
      const settle =
        opts.blePostWriteSettleMs !== undefined
          ? opts.blePostWriteSettleMs
          : parsePositiveIntEnv(import.meta.env?.VITE_PRINTER_BLE_POST_WRITE_SETTLE_MS, 0, 0, 500);
      this.blePostWriteSettleMs = Math.min(500, Math.max(0, settle));
    }

    this.connectModelId = modelAtConstruct;
  }

  getState(): PrinterConnectionState {
    return this.state;
  }

  getDiagnostics(): BleTransportDiagnostics | null {
    return this.lastDiagnostics;
  }

  private setState(next: PrinterConnectionState): void {
    this.state = next;
    this.opts.onStateChange?.();
  }

  /** Uses live stored model so print path stays aligned with encodeReceipt after model changes. */
  private isXpP203a(): boolean {
    return getStoredPrinterModelId() === 'xp-p203a';
  }

  private shouldWakeBeforeWrite(isTspl: boolean): boolean {
    if (isTspl) return false;
    return this.isXpP203a() && shouldPrependXpP203aWake(this.bridgeProfile);
  }

  private resolveXpP203aWriteTiming(): {
    chunkBytes: number;
    interChunkDelayMs: number;
    postWriteSettleMs: number;
    isscDelayOnlyChunkMs: number;
    isscDelayOnlyTsplChunkMs: number;
    isscTsplInterChunkMs: number;
  } {
    return resolveXpP203aBleTiming(this.bridgeProfile);
  }

  private assertP203aWriteWithoutResponse(ch: BluetoothRemoteGATTCharacteristic): void {
    const wwr = (ch as unknown as { writeValueWithoutResponse?: (v: BufferSource) => Promise<void> })
      .writeValueWithoutResponse;
    if (typeof wwr !== 'function' || !ch.properties.writeWithoutResponse) {
      throw new Error(P203A_REQUIRES_WNR_ERROR);
    }
  }

  async connect(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.bluetooth) {
      throw new Error(
        'Web Bluetooth is not available. Use Chrome or Edge on HTTPS/localhost and enable Bluetooth.',
      );
    }

    const uuids = resolvePrinterBleUuids();
    const optionalServices = mergeBleOptionalServicesForRequest(uuids?.service);
    const pickBleOpts = this.isXpP203a() ? getXpP203aBlePickOptions() : undefined;

    this.setState('connecting');
    try {
      const device = await navigator.bluetooth.requestDevice({
        optionalServices: optionalServices as Parameters<Bluetooth['requestDevice']>[0]['optionalServices'],
        acceptAllDevices: true,
      });
      const server = await device.gatt?.connect();
      if (!server) {
        throw new Error('GATT server unavailable');
      }

      const picked = await resolveBleWriteCharacteristic(server, uuids, pickBleOpts);

      this.bridgeProfile = picked.bridgeProfile;

      const isscPreferWriteWithResponse =
        picked.bridgeProfile === 'issc' &&
        (shouldXpP203aIsscWriteWithResponse() ||
          envTruthy(import.meta.env?.VITE_PRINTER_ISSC_WRITE_WITH_RESPONSE));

      if (this.isXpP203a() && !isscPreferWriteWithResponse) {
        this.assertP203aWriteWithoutResponse(picked.characteristic);
      }
      this.device = device;
      this.characteristic = picked.characteristic;

      let isscNotifyEnabled = false;
      let isscAirPatchEnabled = false;
      if (this.bridgeProfile === 'issc') {
        const airPatch = await tryEnableIsscAirPatch(server, picked.serviceUuid);
        isscAirPatchEnabled = airPatch.enabled;
        const notify = await tryEnableIsscNotifyHandshake(server, picked.serviceUuid);
        isscNotifyEnabled = notify.enabled;
        this.isscCreditGate = notify.creditGate ?? null;
        this.isscNotifyCharacteristic = notify.notifyCharacteristic ?? null;
        if (this.isscCreditGate) {
          const boot = await this.isscCreditGate.bootstrapCredits(1200, 6);
          this.isscDelayOnlyPacing = boot.usedFallback;
          printerDebug('ISSC credit bootstrap complete', {
            credits: boot.credits,
            usedFallback: boot.usedFallback,
            creditFlow: boot.usedFallback ? 'delay-only' : 'notify',
          });
        }
      }

      if (this.bridgeProfile === 'issc' && isscNotifyEnabled) {
        await delayMs(ISSC_BLE_POST_CONNECT_MS);
        printerDebug('ISSC post-connect ready delay', { ms: ISSC_BLE_POST_CONNECT_MS });
      }

      const p203aTiming = this.isXpP203a() ? this.resolveXpP203aWriteTiming() : null;
      const canWnr = Boolean(picked.characteristic.properties.writeWithoutResponse);
      const wakeOnWrite = this.shouldWakeBeforeWrite(false);
      this.lastDiagnostics = {
        deviceName: (device as BluetoothDevice & { name?: string }).name ?? '',
        serviceUuid: picked.serviceUuid,
        characteristicUuid: picked.characteristicUuid,
        writeMode: canWnr ? 'writeWithoutResponse' : 'writeValue',
        chunkBytes: p203aTiming?.chunkBytes ?? this.bleChunkBytes,
        interChunkDelayMs: p203aTiming?.interChunkDelayMs ?? this.bleInterChunkDelayMs,
        postWriteSettleMs: p203aTiming?.postWriteSettleMs ?? this.blePostWriteSettleMs,
        printerModelId: getStoredPrinterModelId(),
        writeWithoutResponseAvailable: canWnr,
        bridgeProfile: this.bridgeProfile,
        wakeOnWrite,
        isscNotifyEnabled,
        isscCreditFlow:
          this.bridgeProfile === 'issc'
            ? this.isscDelayOnlyPacing
              ? 'delay-only'
              : 'notify'
            : undefined,
        isscAirPatchEnabled: this.bridgeProfile === 'issc' ? isscAirPatchEnabled : undefined,
        explicitUuidFallback: Boolean(picked.explicitUuidFallback),
      };

      printerDebug('BLE connected', {
        deviceName: (device as BluetoothDevice & { name?: string }).name,
        storedModelId: getStoredPrinterModelId(),
        connectModelId: this.connectModelId,
        ...this.lastDiagnostics,
        pickScore: picked.score,
        write: Boolean(picked.characteristic.properties.write),
      });
    } catch (e) {
      this.setState('error');
      await this.cleanup();
      throw e instanceof Error ? e : new Error(String(e));
    }

    this.gattDisconnectHandler = () => {
      void this.onGattServerDisconnected();
    };
    this.device!.addEventListener('gattserverdisconnected', this.gattDisconnectHandler);
    this.setState('connected');
  }

  async disconnect(): Promise<void> {
    await this.cleanup();
    this.setState('disconnected');
  }

  private async onGattServerDisconnected(): Promise<void> {
    if (this.device && this.gattDisconnectHandler) {
      this.device.removeEventListener('gattserverdisconnected', this.gattDisconnectHandler);
    }
    this.gattDisconnectHandler = null;
    this.characteristic = null;
    this.isscNotifyCharacteristic = null;
    this.isscCreditGate = null;
    this.isscDelayOnlyPacing = false;
    this.device = null;
    this.bridgeProfile = 'generic';
    this.lastDiagnostics = null;
    this.setState('disconnected');
    this.opts.onGattServerDisconnected?.();
  }

  private async cleanup(): Promise<void> {
    try {
      if (this.isscNotifyCharacteristic) {
        try {
          const notifyCh = this.isscNotifyCharacteristic as BluetoothRemoteGATTCharacteristic & {
            stopNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
          };
          await notifyCh.stopNotifications();
        } catch {
          /* ignore */
        }
      }
      if (this.device && this.gattDisconnectHandler) {
        this.device.removeEventListener('gattserverdisconnected', this.gattDisconnectHandler);
      }
      this.gattDisconnectHandler = null;
      this.characteristic = null;
      this.isscNotifyCharacteristic = null;
      this.isscCreditGate = null;
      this.isscDelayOnlyPacing = false;
      this.bridgeProfile = 'generic';
      this.lastDiagnostics = null;
      if (this.device?.gatt?.connected) {
        await Promise.resolve(this.device.gatt.disconnect());
      }
      this.device = null;
    } catch {
      this.device = null;
      this.characteristic = null;
      this.isscNotifyCharacteristic = null;
      this.isscCreditGate = null;
      this.isscDelayOnlyPacing = false;
      this.gattDisconnectHandler = null;
      this.bridgeProfile = 'generic';
      this.lastDiagnostics = null;
    }
  }

  /** Lone CR after job — some ISSC UART bridges only commit the buffer on 0x0D. */
  private async isscTrailingUartFlush(
    ch: BluetoothRemoteGATTCharacteristic,
    wwr: (v: BufferSource) => Promise<void>,
  ): Promise<void> {
    await delayMs(ISSC_BLE_UART_FLUSH_MS);
    await wwr.call(ch, new Uint8Array([0x0d]));
    await delayMs(ISSC_BLE_UART_FLUSH_MS);
    printerDebug('ISSC UART trailing CR flush sent');
  }

  async writeAuxiliary(bytes: Uint8Array): Promise<void> {
    await this.writePayload(bytes, { auxiliary: true });
  }

  async write(bytes: Uint8Array): Promise<void> {
    await this.writePayload(bytes, { auxiliary: false });
  }

  private async writePayload(bytes: Uint8Array, opts: { auxiliary: boolean }): Promise<void> {
    if (!this.characteristic) {
      throw new Error('Bluetooth printer not connected');
    }
    if (!opts.auxiliary) {
      this.setState('printing');
    }
    try {
      const ch = this.characteristic;
      const wwr = (ch as unknown as { writeValueWithoutResponse?: (v: BufferSource) => Promise<void> })
        .writeValueWithoutResponse;

      const canWriteWithoutResponse =
        typeof wwr === 'function' && Boolean(ch.properties.writeWithoutResponse);

      const p203a = this.isXpP203a();
      if (p203a && !canWriteWithoutResponse) {
        throw new Error(P203A_REQUIRES_WNR_ERROR);
      }

      const isscPreferWriteWithResponse =
        this.bridgeProfile === 'issc' &&
        (shouldXpP203aIsscWriteWithResponse() ||
          envTruthy(import.meta.env?.VITE_PRINTER_ISSC_WRITE_WITH_RESPONSE));

      const useWithoutResponse = p203a
        ? isscPreferWriteWithResponse
          ? false
          : canWriteWithoutResponse
        : !this.blePreferWriteWithResponse && canWriteWithoutResponse;

      const writeMode: BleTransportDiagnostics['writeMode'] = useWithoutResponse
        ? 'writeWithoutResponse'
        : 'writeValue';

      const p203aTiming = p203a ? this.resolveXpP203aWriteTiming() : null;
      const step = p203aTiming
        ? Math.min(p203aTiming.chunkBytes, getXpP203aAttChunkCap(useWithoutResponse))
        : useWithoutResponse
          ? Math.min(this.bleChunkBytes, BLE_ATT_WRITE_WITHOUT_RESPONSE_MAX)
          : this.bleChunkBytes;

      const isTspl = p203a && isLikelyTsplPayload(bytes);
      const interDelay = p203aTiming?.interChunkDelayMs ?? this.bleInterChunkDelayMs;
      const postSettle = p203aTiming?.postWriteSettleMs ?? this.blePostWriteSettleMs;
      let isscAfterChunkMs =
        this.bridgeProfile === 'issc' && this.isscDelayOnlyPacing
          ? (p203aTiming?.isscDelayOnlyChunkMs ?? ISSC_BLE_DELAY_ONLY_CHUNK_MS)
          : interDelay;
      if (isTspl && this.bridgeProfile === 'issc') {
        if (this.isscDelayOnlyPacing) {
          isscAfterChunkMs = p203aTiming?.isscDelayOnlyTsplChunkMs ?? ISSC_BLE_DELAY_ONLY_TSPL_CHUNK_MS;
        } else {
          isscAfterChunkMs = Math.max(
            isscAfterChunkMs,
            p203aTiming?.isscTsplInterChunkMs ?? ISSC_BLE_TSPL_INTER_CHUNK_DELAY_MS,
          );
        }
      }

      let data = bytes;
      const wakePrepended = this.shouldWakeBeforeWrite(isTspl);
      if (wakePrepended) {
        const wake = new Uint8Array(XP_P203A_BLE_WAKE_BYTES);
        data = concatBytes(wake, bytes);
      }

      const tsplChunks = isTspl ? splitTsplPayloadForBleChunks(data, step) : null;
      const chunkCount = tsplChunks ? tsplChunks.length : Math.ceil(data.length / step);
      const tsplLineEnds = tsplChunks?.map((c) => c.endsLine);
      const storedModelId = getStoredPrinterModelId();
      if (this.connectModelId && storedModelId !== this.connectModelId) {
        printerDebugWarn('BLE model changed since connect; write uses current stored model', {
          connectModelId: this.connectModelId,
          storedModelId,
        });
      }

      printerDebug(opts.auxiliary ? 'BLE auxiliary write start' : 'BLE write start', {
        escPosBytes: bytes.length,
        payloadWithWake: data.length,
        wakePrepended,
        isTspl,
        bridgeProfile: this.bridgeProfile,
        preview: opts.auxiliary
          ? hexPreview(bytes)
          : isTspl
            ? previewTsplBytes(bytes)
            : previewEscPosBytes(bytes),
        payloadHexPreview: hexPreview(data),
        writeMode,
        chunkBytes: step,
        chunkCount,
        chunkSizes: tsplChunks
          ? tsplChunks.map((c) => c.bytes.length)
          : summarizeChunkSizes(data.length, step),
        tsplLineEnds,
        interChunkDelayMs: interDelay,
        isscAfterChunkMs,
        isscDelayOnlyPacing: this.bridgeProfile === 'issc' ? this.isscDelayOnlyPacing : undefined,
        postWriteSettleMs: postSettle,
        storedModelId,
        connectModelId: this.connectModelId,
        serviceUuid: this.lastDiagnostics?.serviceUuid,
        characteristicUuid: this.lastDiagnostics?.characteristicUuid,
      });

      if (this.lastDiagnostics) {
        this.lastDiagnostics = {
          ...this.lastDiagnostics,
          writeMode,
          chunkBytes: step,
          printerModelId: storedModelId,
          writeWithoutResponseAvailable: canWriteWithoutResponse,
          wakeOnWrite: wakePrepended,
          isscCreditFlow:
            this.bridgeProfile === 'issc'
              ? this.isscDelayOnlyPacing
                ? 'delay-only'
                : 'notify'
              : this.lastDiagnostics.isscCreditFlow,
        };
      }

      const writeChunk = async (chunk: Uint8Array): Promise<void> => {
        if (this.bridgeProfile === 'issc' && this.isscCreditGate && !this.isscDelayOnlyPacing) {
          await this.isscCreditGate.waitForCredit(1500);
        }
        const payload = new Uint8Array(chunk.byteLength);
        payload.set(chunk);
        if (useWithoutResponse) {
          await wwr!.call(ch, payload);
        } else {
          await ch.writeValue(payload);
        }
      };

      if (tsplChunks) {
        for (let i = 0; i < tsplChunks.length; i += 1) {
          const { bytes: chunkBytes, endsLine } = tsplChunks[i]!;
          await writeChunk(chunkBytes);
          const hasMore = i + 1 < tsplChunks.length;
          const pauseAfterChunk = hasMore && endsLine;
          if (this.bridgeProfile === 'issc') {
            const gap = pauseAfterChunk ? isscAfterChunkMs : 0;
            if (gap > 0) await delayMs(gap);
          } else if (interDelay > 0 && pauseAfterChunk) {
            await delayMs(interDelay);
          }
        }
      } else {
        for (let i = 0; i < data.length; i += step) {
          await writeChunk(data.subarray(i, i + step));
          const hasMore = i + step < data.length;
          if (this.bridgeProfile === 'issc') {
            const gap = hasMore ? isscAfterChunkMs : 0;
            if (gap > 0) await delayMs(gap);
          } else if (interDelay > 0 && hasMore) {
            await delayMs(interDelay);
          }
        }
      }

      if (this.bridgeProfile === 'issc' && useWithoutResponse && typeof wwr === 'function' && !opts.auxiliary) {
        await this.isscTrailingUartFlush(ch, wwr);
      }

      if (postSettle > 0 && !opts.auxiliary) {
        await delayMs(postSettle);
      }

      printerDebug(opts.auxiliary ? 'BLE auxiliary write complete' : 'BLE write complete', {
        chunkCount,
        writeMode,
        wakePrepended,
        bridgeProfile: this.bridgeProfile,
      });

      if (!opts.auxiliary && p203a && p203aTiming) {
        recordXpP203aBlePrintSuccess(this.bridgeProfile, {
          chunkBytes: step,
          interChunkDelayMs: interDelay,
          postWriteSettleMs: postSettle,
          isscDelayOnlyChunkMs: p203aTiming.isscDelayOnlyChunkMs,
          isscDelayOnlyTsplChunkMs: p203aTiming.isscDelayOnlyTsplChunkMs,
          isscTsplInterChunkMs: p203aTiming.isscTsplInterChunkMs,
        });
      }

      if (!opts.auxiliary) {
        this.setState('connected');
      }
    } catch (e) {
      const stillLinked = Boolean(this.device?.gatt?.connected && this.characteristic);
      if (stillLinked) {
        if (!opts.auxiliary) {
          this.setState('connected');
        }
      } else {
        await this.cleanup();
        this.setState('disconnected');
        this.opts.onGattServerDisconnected?.();
      }
      throw e instanceof Error ? e : new Error(String(e));
    }
  }
}
